import { APP_CONFIG } from '@/constants/app';
import { unwrapApiData } from '@/utils/api-response';
import { getDefaultTemplate } from '@/template-mail';
import { ApiResponse } from '@/types/auth.types';
import {
  DomainDetailResponse,
  Domain,
  DomainsListResponse,
  DomainsQueryParams,
  DsnResponse,
  EmailTemplateFormData,
  EmailTemplateResponse,
  EmailTemplatesListResponse,
  EstablishmentsResponse,
  FolderDetailResponse,
  FoldersListResponse,
  FoldersQueryParams,
  GlobalFoldersQueryParams,
  SendBulletinsResponse,
  SyncDomainsResponse,
} from '@/types/domain.types';
import { EnvoiDocument } from '@/types/envoi.types';
import { tokenManager } from '@/utils/token-manager';
import axios from 'axios';

// Create axios instance for domains service
const domainsApi = axios.create({
  baseURL: APP_CONFIG.api.baseUrl + '/domains',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Setup automatic token refresh interceptors
tokenManager.setupInterceptors(domainsApi);

export const requireSuccessfulSync = (
  result: SyncDomainsResponse
): SyncDomainsResponse => {
  if (!result.success) {
    throw new Error(
      result.error || result.message || 'La mise à jour OpenPaye a échoué'
    );
  }

  return result;
};

export const domainsService = {
  // Get domains list with pagination and search
  getDomains: async (
    params: DomainsQueryParams = {}
  ): Promise<DomainsListResponse> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    const response = await domainsApi.get<DomainsListResponse>(
      `?${queryParams.toString()}`
    );
    return response.data;
  },

  // Get single domain by UUID
  getDomain: async (uuid: string): Promise<DomainDetailResponse> => {
    const response = await domainsApi.get<DomainDetailResponse>(`/${uuid}`);
    return response.data;
  },

  // Get folders for a domain
  getDomainFolders: async (
    domainUuid: string,
    params: FoldersQueryParams = {}
  ): Promise<FoldersListResponse> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.syncStatus) queryParams.append('syncStatus', params.syncStatus);
    if (params.domainName) queryParams.append('domainName', params.domainName);
    if (params.responsable)
      queryParams.append('responsable', params.responsable);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await domainsApi.get<FoldersListResponse>(
      `/${domainUuid}/folders?${queryParams.toString()}`
    );
    return response.data;
  },

  // Sync domains from OpenPaye
  syncDomainsFromOpenPaye: async (): Promise<SyncDomainsResponse> => {
    const response = await domainsApi.post<SyncDomainsResponse>(
      '/sync-domains-from-openpay',
      {}
    );
    return requireSuccessfulSync(response.data);
  },

  // Update domain settings
  updateDomain: async (
    uuid: string,
    data: Partial<{
      isActive: boolean;
      autoSync: boolean;
      syncFrequency: Domain['syncFrequency'];
      description: string;
    }>
  ): Promise<ApiResponse<unknown>> => {
    const response = await domainsApi.patch<ApiResponse<unknown>>(
      `/${uuid}`,
      data
    );
    return response.data;
  },

  // Trigger manual sync for a domain
  syncDomain: async (uuid: string): Promise<SyncDomainsResponse> => {
    const response = await domainsApi.post<SyncDomainsResponse>(`/${uuid}/sync`);
    return requireSuccessfulSync(response.data);
  },

  // Get all folders globally (across all domains)
  getAllFolders: async (
    params: GlobalFoldersQueryParams = {}
  ): Promise<FoldersListResponse> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.syncStatus) queryParams.append('syncStatus', params.syncStatus);
    if (params.domainName) queryParams.append('domainName', params.domainName);
    if (params.responsable)
      queryParams.append('responsable', params.responsable);
    if (params.scope) queryParams.append('scope', params.scope);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    const response = await domainsApi.get<FoldersListResponse>(
      `/folders?${queryParams.toString()}`
    );
    return response.data;
  },

  // Get aggregated responsables for the authenticated user (across all domains)
  getResponsables: async (): Promise<{ name: string; email?: string }[]> => {
    const response = await domainsApi.get<{
      data: { name: string; email?: string }[];
    }>('/responsables');
    // Some endpoints in this project wrap payload in { data }, align with that
    return unwrapApiData<{ name: string; email?: string }[]>(response.data);
  },

  // Get folder details by UUID
  getFolderDetail: async (
    folderUuid: string
  ): Promise<FolderDetailResponse> => {
    const response = await domainsApi.get<FolderDetailResponse>(
      `/folders/${folderUuid}`
    );
    return response.data;
  },

  /**
   * Les documents envoyés par courriel pour ce dossier — une entrée par
   * destinataire. Demandé le 01/09/2026 : « je n'ai aucun moyen de vérifier
   * que le client l'a lui aussi bien reçu ».
   */
  getFolderEnvois: async (
    folderUuid: string,
    limit = 50
  ): Promise<EnvoiDocument[]> => {
    const response = await domainsApi.get<{ data: EnvoiDocument[] }>(
      `/folders/${folderUuid}/envois?limit=${limit}`
    );
    return response.data.data;
  },

  // Get establishments for a folder
  getFolderEstablishments: async (
    folderUuid: string,
    params: { page?: number; limit?: number } = {}
  ): Promise<EstablishmentsResponse> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const response = await domainsApi.get<EstablishmentsResponse>(
      `/establishments/folder/${folderUuid}?${queryParams.toString()}`
    );
    return response.data;
  },

  // Get DSN structured data for a folder
  getDsnStructured: async (
    folderUuid: string,
    params: {
      month: number;
      year: number;
      email: string;
    }
  ): Promise<DsnResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.append('month', params.month.toString());
    queryParams.append('year', params.year.toString());
    queryParams.append('emailToReceviedDSN', params.email);

    const response = await domainsApi.get<DsnResponse>(
      `/folders/${folderUuid}/dsn/structured?${queryParams.toString()}`
    );
    return response.data;
  },

  // Send bulletins by email for a folder
  sendBulletins: async (
    folderUuid: string,
    params: {
      month: number;
      year: number;
      email: string;
    }
  ): Promise<SendBulletinsResponse> => {
    const response = await domainsApi.post<{ data: SendBulletinsResponse }>(
      `/folders/${folderUuid}/send-bulletins`,
      {
        month: params.month,
        year: params.year,
        email: params.email,
      }
    );
    return response.data.data;
  },

  // Download bulletins as PDF for a folder
  downloadBulletins: async (
    folderUuid: string,
    params: {
      month: number;
      year: number;
      saveToGed?: boolean;
    }
  ): Promise<Blob> => {
    const response = await domainsApi.post(
      `/folders/${folderUuid}/download-bulletins`,
      {
        month: params.month,
        year: params.year,
        saveToGed: params.saveToGed || false,
      },
      {
        responseType: 'blob',
      }
    );
    return response.data;
  },

  // Sync folder data (establishments, employees, contracts)
  syncFolderData: async (
    domainUuid: string,
    folderUuid: string
  ): Promise<SyncDomainsResponse> => {
    const response = await domainsApi.post<SyncDomainsResponse>(
      `/${domainUuid}/folders/${folderUuid}/sync`
    );
    return requireSuccessfulSync(response.data);
  },

  // Email Templates Management
  // Get email templates for a folder
  getEmailTemplates: async (
    folderUuid: string,
    templateType?: 'dsn' | 'bulletins' | 'editions'
  ): Promise<EmailTemplatesListResponse> => {
    const queryParams = new URLSearchParams();
    if (templateType) {
      queryParams.append('templateType', templateType);
    }

    const response = await domainsApi.get<EmailTemplatesListResponse>(
      `/folders/${folderUuid}/email-templates?${queryParams.toString()}`
    );
    return response.data;
  },

  // Get single email template
  getEmailTemplate: async (
    folderUuid: string,
    templateUuid: string
  ): Promise<EmailTemplateResponse> => {
    const response = await domainsApi.get<EmailTemplateResponse>(
      `/folders/${folderUuid}/email-templates/${templateUuid}`
    );
    return response.data;
  },

  // Create new email template
  createEmailTemplate: async (
    folderUuid: string,
    templateData: EmailTemplateFormData & {
      templateType: 'dsn' | 'bulletins' | 'editions';
    }
  ): Promise<EmailTemplateResponse> => {
    const response = await domainsApi.post<EmailTemplateResponse>(
      `/folders/${folderUuid}/email-templates`,
      templateData
    );
    return response.data;
  },

  // Update email template
  updateEmailTemplate: async (
    folderUuid: string,
    templateUuid: string,
    templateData: EmailTemplateFormData
  ): Promise<EmailTemplateResponse> => {
    const response = await domainsApi.patch<EmailTemplateResponse>(
      `/folders/${folderUuid}/email-templates/${templateUuid}`,
      templateData
    );
    return response.data;
  },

  // Delete email template
  deleteEmailTemplate: async (
    folderUuid: string,
    templateUuid: string
  ): Promise<ApiResponse<unknown>> => {
    const response = await domainsApi.delete<ApiResponse<unknown>>(
      `/folders/${folderUuid}/email-templates/${templateUuid}`
    );
    return response.data;
  },

  // Get default email template
  getDefaultEmailTemplate: async (
    templateType: 'dsn' | 'bulletins' | 'editions'
  ): Promise<{ subject: string; htmlContent: string }> => {
    // Return the default template from template-mail.ts
    const defaultTemplate = getDefaultTemplate(templateType);
    if (!defaultTemplate) {
      throw new Error(`No default template found for type: ${templateType}`);
    }
    return {
      subject: defaultTemplate.subject,
      htmlContent: defaultTemplate.html,
    };
  },
};
